import { moveItemInArray } from '@angular/cdk/drag-drop';
import { routerNavigatedAction } from '@ngrx/router-store';
import { on } from '@ngrx/store';

import { JobDetailsV2Fragment, Job_CalendarEventFragment, PriceType } from 'graphql.generated';
import { cloneDeep } from 'lodash';

import { eventTypeInfoMapV2 } from '../../global.constants';
import { DiscardOpportunityDialogActions } from '../job-create/discard-job-component/discard-job.actions';
import { JobToolSubscriptionActions } from '../job-state/job-tool-subscription.actions';
import { JobToolActions } from '../job-tool.actions';
import { JobMutateObjectActions } from '../mutate-object/mutate-object.actions';

import { WorkOrdersActions } from './job-workorders.actions';
import { WorkorderEventAttachment } from './job-workorders.interfaces';
import { EditableCharge, EditableDiscount, EditableWorkorder, JobWorkordersState, jobWorkordersInitialState } from './job-workorders.state';

/**
 * Event metadata round-trips values as JSON strings (Neo4j primitive rule) —
 * tolerate both the string and the already-parsed array shape (#6983:
 * files attached to THE event via the Event Notes 📎).
 */
/** Only http(s) URLs may be opened from an attachment chip — the url comes
 *  from event metadata anyone with event-write can author, so a
 *  `javascript:` (or other scheme) value must not reach `window.open`
 *  (CodeRabbit round 17). */
function isSafeAttachmentUrl(url: unknown): boolean {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

function parseWorkorderEventAttachments(raw: unknown): WorkorderEventAttachment[] | undefined {
  if (!raw) return undefined;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return undefined;
    // Keep only well-formed entries — null / half-written values would blow
    // the template's attachment.name access (CodeRabbit: validate entries).
    const entries = parsed
      .filter((entry): entry is WorkorderEventAttachment =>
        !!entry && typeof entry === 'object' && !Array.isArray(entry)
        && typeof (entry as WorkorderEventAttachment).artifactId === 'string'
        && typeof (entry as WorkorderEventAttachment).name === 'string')
      // An unsafe scheme does not poison the entry: the artifactId download
      // path stays legitimate, only the click-through url is dropped.
      .map((entry) => (entry.url === undefined || isSafeAttachmentUrl(entry.url)
        ? entry
        : { ...entry, url: undefined }));
    return entries;
  } catch {
    return undefined;
  }
}

// [#6100]: CDK drop index is in visible-list space; chargesWithRemoved may contain
// removed entries that shift positions. chargeId lookup is intentional.
function getInsertAt(chargesWithRemoved: EditableCharge[], visibleCharges: EditableCharge[], visibleTargetIndex: number): number {
  const anchorCharge = visibleCharges[visibleTargetIndex];
  if (anchorCharge) {
    return chargesWithRemoved.findIndex((c) => c.chargeId === anchorCharge.chargeId);
  }
  const lastVisibleCharge = visibleCharges[visibleCharges.length - 1];
  return lastVisibleCharge
    ? chargesWithRemoved.findIndex((c) => c.chargeId === lastVisibleCharge.chargeId) + 1
    : chargesWithRemoved.length;
}

export const jobWorkordersReducers = [
    on(
      JobToolActions.jobLoaded,
      JobToolSubscriptionActions.remoteJobUpdated,
      WorkOrdersActions.workOrdersReloaded,
      (state: JobWorkordersState, { job }): JobWorkordersState => {
        // In-flight upload counts survive same-job refreshes (workOrdersReloaded
        // / remoteJobUpdated) — dropping them zeroes the save gate mid-upload
        // (CodeRabbit). Different job identity = the stale counters belong to
        // the previous job and MUST go, or saves on the new job block forever.
        const isDifferentJob = state.loadedJobId !== undefined && state.loadedJobId !== job.id;
        return {
          ...state,
          loadedJobId: job.id,
          workordersEditor: {
            workorders: getJobWorkorders(job),
            actionsMenuEventId: undefined,
            actionsMenuChargeId: undefined,
            actionsMenuAppliedDiscountId: undefined,
            collapsed: {},
            confidentialInfoExpanded: {},
            eventUploads: isDifferentJob ? {} : (state.workordersEditor.eventUploads ?? {}),
          },
        }
        },

    ),
    on(JobToolActions.saveChangesSuccess, (state: JobWorkordersState, { updatedJob }): JobWorkordersState => {
        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                // Refresh workorders model
                workorders: getJobWorkorders(updatedJob),
            }
        }
    }),
    on(JobToolActions.jobDataReset, (state: JobWorkordersState, { job }): JobWorkordersState => {
        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders: getJobWorkorders(job),
            }
        }
    }),
    // Menu item functionality
    on(WorkOrdersActions.openEventActionsButtonClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                actionsMenuEventId: eventId,
            },
        }
    }),
    // Event reordering functionality
    on(WorkOrdersActions.moveToTopMenuItemClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        let workorders = cloneDeep(state.workordersEditor.workorders);

        const currentIndex = workorders.findIndex((wo) => wo.eventId === eventId);

        const newIndex = 0;

        moveItemInArray(workorders, currentIndex, newIndex);

        workorders = workorders.map((wo) => ({ ...wo, modified: true }));

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
                actionsMenuEventId: undefined,
            }
        };
    }),
    on(WorkOrdersActions.moveUpMenuItemClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        let workorders = cloneDeep(state.workordersEditor.workorders);

        const currentIndex = workorders.findIndex((wo) => wo.eventId === eventId);

        const newIndex = currentIndex - 1;

        moveItemInArray(workorders, currentIndex, newIndex);

        workorders = workorders.map((wo) => ({ ...wo, modified: true }));

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
                actionsMenuEventId: undefined,
            }
        };
    }),
    on(WorkOrdersActions.moveDownMenuItemClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        let workorders = cloneDeep(state.workordersEditor.workorders);

        const currentIndex = workorders.findIndex((wo) => wo.eventId === eventId);

        const newIndex = currentIndex + 1;

        moveItemInArray(workorders, currentIndex, newIndex);

        workorders = workorders.map((wo) => ({ ...wo, modified: true }));

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
                actionsMenuEventId: undefined,
            }
        };
    }),
    on(WorkOrdersActions.moveToBottomMenuItemClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        let workorders = cloneDeep(state.workordersEditor.workorders);

        const currentIndex = workorders.findIndex((wo) => wo.eventId === eventId);

        const newIndex = workorders.length - 1;

        moveItemInArray(workorders, currentIndex, newIndex);

        workorders = workorders.map((wo) => ({ ...wo, modified: true }));

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
                actionsMenuEventId: undefined,
            }
        };
    }),
    // Collapse functionality
    on(WorkOrdersActions.toggleEventButtonClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        const current = state.workordersEditor.collapsed[eventId] || false;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                collapsed: {
                    ...state.workordersEditor.collapsed,
                    [ eventId ]: !current,
                },
            },
        }
    }),
    on(WorkOrdersActions.toggleAllEventsButtonClicked, (state: JobWorkordersState): JobWorkordersState => {

        const allEventsCollapsed = state.workordersEditor.workorders
            .every((wo) => state.workordersEditor.collapsed[wo.eventId]);

        // If all events collapsed, expand all
        if (allEventsCollapsed) {
            return {
                ...state,
                workordersEditor: {
                    ...state.workordersEditor,
                    collapsed: {},
                },
            }
        }

        const collapsed = {};

        for (const workorder of state.workordersEditor.workorders) {
            collapsed[workorder.eventId] = true;
        }

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                collapsed,
            },
        }

    }),
    //Edit charge functionality
    on(WorkOrdersActions.rateInputChanged, (state: JobWorkordersState, { eventId, chargeId, amount }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);
        if (!workorder) { return state; }

        const charge = workorder.charges?.find((c) => c.chargeId === chargeId);
        if (!charge) { return state; }

        charge.amount = amount;

        charge.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            }
        }
    }),
    on(WorkOrdersActions.rateInputKeyDown, (state: JobWorkordersState, { eventId, chargeId, event }): JobWorkordersState => {

        if (event.key !== 'Backspace' && event.key !== 'Delete') { return state; }

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);
        if (!workorder) { return state; }

        const charge = workorder.charges?.find((c) => c.chargeId === chargeId);
        if (!charge) { return state; }

        const currentAmount = charge.amount;

        if (currentAmount === 0 || currentAmount === null || currentAmount === undefined) {

            charge.amount = null;

            charge.modified = true;
        }

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            }
        }
    }),
    on(WorkOrdersActions.quantityInputChanged, (state: JobWorkordersState, { eventId, chargeId, quantity }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);
        if (!workorder) { return state; }

        const charge = workorder.charges?.find((c) => c.chargeId === chargeId);
        if (!charge) { return state; }

        charge.quantity = quantity;

        charge.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            }
        }
    }),
    on(WorkOrdersActions.chargeDescriptionChanged, (state: JobWorkordersState, { eventId, chargeId, description }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);

        if (!workorder) {
            return state;
        }

        const charge = workorder.charges?.find((c) => c.chargeId === chargeId);

        if (!charge) {
            return state;
        }

        charge.description = description;

        charge.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
        }
    }),
    on(WorkOrdersActions.notesChanged, (state: JobWorkordersState, { eventId, eventNotes }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);

        if (!workorder) {
            return state;
        }

        if (workorder.eventNotes === eventNotes) {
            return state;
        }

        workorder.eventNotes = eventNotes;

        workorder.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
        }
    }),
    // Event Notes 📎 upload bookkeeping — saves wait on this so they cannot
    // snapshot the attachment list while a file is still uploading
    // (CodeRabbit: "Keep saves from overtaking an attachment upload").
    on(WorkOrdersActions.eventUploadStarted, (state: JobWorkordersState, { eventId }): JobWorkordersState => ({
        ...state,
        workordersEditor: {
            ...state.workordersEditor,
            eventUploads: { ...state.workordersEditor.eventUploads, [eventId]: (state.workordersEditor.eventUploads[eventId] ?? 0) + 1 },
        },
    })),
    on(WorkOrdersActions.eventUploadFinished, (state: JobWorkordersState, { eventId }): JobWorkordersState => ({
        ...state,
        workordersEditor: {
            ...state.workordersEditor,
            eventUploads: {
              ...state.workordersEditor.eventUploads,
              [eventId]: Math.max(0, (state.workordersEditor.eventUploads[eventId] ?? 0) - 1),
            },
        },
    })),
    on(WorkOrdersActions.eventAttachmentsChanged, (state: JobWorkordersState, { eventId, eventAttachments }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);

        if (!workorder) {
            return state;
        }

        workorder.eventAttachments = eventAttachments;

        workorder.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
        }
    }),
    // Atomic per-event attachment changes (CodeRabbit): both handlers read
    // the CURRENT in-reducer list instead of a whole array read-modify-write
    // in the component, so a concurrent upload can never be dropped by
    // another dispatch replacing the list.
    on(WorkOrdersActions.eventAttachmentUploaded, (state: JobWorkordersState, { eventId, attachment }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);

        if (!workorder) {
            return state;
        }

        workorder.eventAttachments = [...(workorder.eventAttachments ?? []), attachment];

        workorder.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
        }
    }),
    on(WorkOrdersActions.removeAttachmentButtonClicked, (state: JobWorkordersState, { eventId, artifactId }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);

        if (!workorder) {
            return state;
        }

        workorder.eventAttachments = (workorder.eventAttachments ?? []).filter((a) => a.artifactId !== artifactId);

        workorder.modified = true;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
        }
    }),
    on(WorkOrdersActions.openAddChargesDialogButtonClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        return {
            ...state,
            addChargesDialog: {
                ...state.addChargesDialog,
                selectedEventId: eventId,
            },
        }
    }),
    on(WorkOrdersActions.productSearchInputChanged, (state: JobWorkordersState, { query }): JobWorkordersState => {
        return {
            ...state,
            addChargesDialog: {
                ...state.addChargesDialog,
                productSearch: query,
            },
        }
    }),
    on(WorkOrdersActions.productQuantityInputChanged, (state: JobWorkordersState, { productId, quantity }): JobWorkordersState => {

        const selectedProducts = { ...state.addChargesDialog.selectedProducts };

        selectedProducts[productId] = quantity;

        return {
            ...state,
            addChargesDialog: {
                ...state.addChargesDialog,
                selectedProducts,
            },
        }
    }),
    on(WorkOrdersActions.productQuantitySwitchClicked, (state: JobWorkordersState, { productId }): JobWorkordersState => {

        const selectedProducts = { ...state.addChargesDialog.selectedProducts };

        if (selectedProducts[productId]) {
            delete selectedProducts[productId];
        } else {
            selectedProducts[productId] = 1;
        }

        return {
            ...state,
            addChargesDialog: {
                ...state.addChargesDialog,
                selectedProducts,
            },
        }
    }),
    on(
      WorkOrdersActions.cancelAddChargesButtonClicked,
      WorkOrdersActions.exitAddChargesDialogButtonClicked,
      JobMutateObjectActions.exitMutateObjectDialogButtonClicked,
      JobMutateObjectActions.escapeKeyPressed,
      (state: JobWorkordersState): JobWorkordersState => {
            return {
                ...state,
                addChargesDialog: {
                    ...jobWorkordersInitialState.addChargesDialog,
                },
            }
        },
    ),
    on(WorkOrdersActions.chargesAdded, (state: JobWorkordersState, { charges, eventIds }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        for (const eventId of eventIds) {

            const workorder = workorders?.find((wo) => wo.eventId === eventId);
            if (!workorder) { continue; }

            for (const charge of charges) {
                workorder.charges.push(charge);
            }

            workorder.modified = true;
        }

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
            addChargesDialog: {
                ...jobWorkordersInitialState.addChargesDialog,
            },
        }
    }),
    on(WorkOrdersActions.openChargeActionsButtonClicked, (state: JobWorkordersState, { chargeId }): JobWorkordersState => {
        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                actionsMenuChargeId: chargeId,
            }
        }
    }),
    on(
      WorkOrdersActions.removeChargeButtonClicked,
      WorkOrdersActions.removeChargeMenuItemClicked,
      (state: JobWorkordersState, { chargeId }): JobWorkordersState => {

          const workorders = cloneDeep(state.workordersEditor.workorders);

          for (const workorder of workorders) {

              const charge = workorder.charges?.find((c) => c.chargeId === chargeId);

              if (!charge) { continue; }

              charge.removed = true;
          }

          return {
              ...state,
              workordersEditor: {
                  ...state.workordersEditor,
                  workorders,
                  actionsMenuChargeId: undefined,
              }
          }
        },
    ),
    on(
      WorkOrdersActions.chargeDropped,
      (state: JobWorkordersState, { chargeId, newIndex, sourceEventId, targetEventId }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const sourceWorkorder = workorders?.find((wo) => wo.eventId === sourceEventId);
        if (!sourceWorkorder) { return state; }

        const sourceIndex = sourceWorkorder.charges.findIndex((c) => c.chargeId === chargeId);
        if (sourceIndex === -1) { return state; }

        if (sourceEventId === targetEventId) {
          const visibleSourceCharges = sourceWorkorder.charges.filter((c) => !c.removed);
          const currentVisibleIndex = visibleSourceCharges.findIndex((c) => c.chargeId === chargeId);
          if (currentVisibleIndex === newIndex) { return state; }
        }

        const [ charge ] = sourceWorkorder.charges.splice(sourceIndex, 1);

        sourceWorkorder.modified = true;

        const targetWorkorder = workorders?.find((wo) => wo.eventId === targetEventId);
        if (!targetWorkorder) { return state; }

        // [#6100]: must filter to visible charges first — unfiltered index is wrong when removed charges are present.
        const visibleChargesInTargetWorkorder = targetWorkorder.charges.filter((c) => !c.removed);
        const insertAt = getInsertAt(targetWorkorder.charges, visibleChargesInTargetWorkorder, newIndex);

        targetWorkorder.charges.splice(insertAt, 0, charge);

        targetWorkorder.modified = true;

        sourceWorkorder.charges.forEach((c) => { if (!c.removed) c.modified = true; });
        if (sourceEventId !== targetEventId) {
          targetWorkorder.charges.forEach((c) => { if (!c.removed) c.modified = true; });
        }

        return {
          ...state,
          workordersEditor: {
            ...state.workordersEditor,
            workorders,
          }
        }
      },
    ),
    on(WorkOrdersActions.eventTypeMenuItemSelected, (state: JobWorkordersState, { eventType, eventId }): JobWorkordersState => {

        const newWorkOrder: EditableWorkorder = {
            eventId,
            charges: [],
            discounts: [],
            eventNotes: '',
            title: eventTypeInfoMapV2[eventType].name,
            type: eventType,
            new: true,
            modified: false,
            removed: false,
            cancelled: false,
        }

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders: [
                    ...state.workordersEditor.workorders,
                    newWorkOrder,
                ]
            }
        }
    }),
    on(WorkOrdersActions.duplicateWorkOrderAdded, (state: JobWorkordersState, { workorder }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders: [ ...workorders, workorder ],
                actionsMenuEventId: undefined,
            }
        }
    }),
    on(WorkOrdersActions.cancelMenuItemClicked, (state: JobWorkordersState): JobWorkordersState => {
        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                actionsMenuEventId: undefined,
            }
        }
    }),
    on(
      WorkOrdersActions.deleteMenuItemClicked,
      (state: JobWorkordersState, { eventId }): JobWorkordersState => {

          const workorders = cloneDeep(state.workordersEditor.workorders);

          const selectedWorkorder = workorders?.find((wo) => wo.eventId === eventId);

          if (selectedWorkorder) {
              selectedWorkorder.removed = true;
          }

          return {
              ...state,
              workordersEditor: {
                  ...state.workordersEditor,
                  workorders,
                  actionsMenuEventId: undefined,
              }
          }
        },
    ),
    on(WorkOrdersActions.showConfidentialInfoToggleButtonClicked, (state: JobWorkordersState, { eventId }): JobWorkordersState => {

        const currentValue = state.workordersEditor.confidentialInfoExpanded[eventId] || false;

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                confidentialInfoExpanded: {
                    ...state.workordersEditor.confidentialInfoExpanded,
                    [eventId]: !currentValue,
                }
            }
        }
    }),
    on(WorkOrdersActions.loadAvailableDiscountsSuccess, (state: JobWorkordersState, res): JobWorkordersState => {
        return {
            ...state,
            availableDiscounts: res.discounts,
        }
    }),
    // A denied discounts.list must not leave the previous zone's list bound to
    // the discount picker — the modal itself has no discounts.list guard.
    on(WorkOrdersActions.availableDiscountsCleared, (state: JobWorkordersState): JobWorkordersState => {
        return {
            ...state,
            availableDiscounts: [],
        }
    }),
    on(WorkOrdersActions.openDiscountActionsButtonClicked, (state: JobWorkordersState, { appliedId }): JobWorkordersState => {
        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                actionsMenuAppliedDiscountId: appliedId,
            }
        }
    }),
    on(WorkOrdersActions.removeDiscountMenuItemClicked, (state: JobWorkordersState, { eventId, appliedId }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        const workorder = workorders?.find((wo) => wo.eventId === eventId);

        if (workorder) {
            const discount = workorder.discounts?.find((d) => d.appliedId === appliedId);
            
            if (discount) {
                discount.removed = true;
            }
        }

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                actionsMenuAppliedDiscountId: undefined,
                workorders,
            }
        }
    }),
    on(WorkOrdersActions.discountAdded, (state: JobWorkordersState, { discount, eventIds }): JobWorkordersState => {

        const workorders = cloneDeep(state.workordersEditor.workorders);

        for (const eventId of eventIds) {

            const workorder = workorders?.find((wo) => wo.eventId === eventId);
            if (!workorder) { continue; }

            workorder.discounts.push(discount);

            workorder.modified = true;
        }

        return {
            ...state,
            workordersEditor: {
                ...state.workordersEditor,
                workorders,
            },
        };
    }),
    on(
      WorkOrdersActions.generateChargesWithAISuccess,
      (state: JobWorkordersState, { eventId, selectedProducts, charges }): JobWorkordersState => {

        const workordersEditor = cloneDeep(state.workordersEditor);

                const workorder = workordersEditor.workorders?.find((wo) => wo.eventId === eventId);
                if (workorder) {
                    for (const charge of charges) {
                        workorder.charges.push(charge);
                    }

                    workorder.modified = true;
                }

        return {
          ...state,
          workordersEditor,
          selectedProducts,
        }
        },
    ),
    on(DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked, (): JobWorkordersState => jobWorkordersInitialState),
    on(routerNavigatedAction, (state: JobWorkordersState, { payload: { event: { urlAfterRedirects } } }): JobWorkordersState => {
      if (urlAfterRedirects.startsWith('/jobs/new')) {
        return jobWorkordersInitialState;
      }
      return state;
    }),
] as const;

function getJobWorkorders(job: JobDetailsV2Fragment) {
    return cloneDeep(job.events)
        .filter((e) => e.status !== 'cancelled')
        .sort((a, b) => a.sequentialOrder - b.sequentialOrder)
        .map((e) => getWorkOrder(e));
}

function getWorkOrder(jobEvent: Job_CalendarEventFragment): EditableWorkorder {
    return {
        eventId: jobEvent.id,
        title: jobEvent.title,
        type: jobEvent.type,
        charges: (jobEvent.charges || [])
        .sort((a, b) => a.order - b.order)
        .map((c): EditableCharge => {

            const chargeAmount = c.amount ?? c.price?.amount ?? 0;

            const priceType = c.price?.priceType || PriceType.Fixed;

            return {
                amount: priceType === PriceType.Fixed ? chargeAmount / 100 : chargeAmount,
                priceType,
                quantity: c.quantity,
                chargeId: c.id,
                productId: c.product?.id,
                productName: c.productName ?? undefined,
                description: c.description ?? undefined,
                new: false,
                modified: false,
                removed: false,
                expenses: c.expensesV2.map((e) => ({
                    expenseId: e.expense.id,
                    unitCost: e.unitCost,
                })),
                taxes: c.taxes.map((t) => ({ id: t.id, percentage: t.percentage })),
            }
        }),
        discounts: (jobEvent.discounts || []).map((d): EditableDiscount => ({
            name: d.discount.name,
            code: d.discount.code,
            appliedId: d.appliedId,
            customAmount: d.appliedCustomAmount,
            discountId: d.discount.id,
            new: false,
            removed: false,
        })),
        eventNotes: (jobEvent.metadata as any)?.eventNotes || '',
        eventAttachments: parseWorkorderEventAttachments((jobEvent.metadata as any)?.eventAttachments),
        new: false,
        modified: false,
        removed: false,
        cancelled: false,
    }
}
